import React, { useState, useEffect } from "react";
import { useApiClient } from "../utils/apiClient";
import { useAuth } from "../hooks/useAuth";
import { XMarkIcon, PlusIcon, TrashIcon, PencilIcon, CheckIcon, ChevronUpIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { USER_COLORS, COLOR_HEX_MAP } from "../constants/colors";

export default function Users() {
    const apiClient = useApiClient();
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    const [adminCreateModalOpen, setAdminCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [adminCreateForm, setAdminCreateForm] = useState({
        email: "",
        user_name: "",
        role: "Provider"
    });
    const [editForm, setEditForm] = useState({
        user_name: "",
        preferences: "",
        role: "Provider",
        user_color: "blue",
        max_monthly_hours: ""
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [sortField, setSortField] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess("");
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError("");
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    async function fetchUsers() {
        setLoading(true);
        setError("");
        try {
            const resp = await apiClient.get("/invites");
            setUsers(resp.users || []);
        } catch (e) {
            setError("Failed to load users");
        } finally {
            setLoading(false);
        }
    }

    function handleAdminCreateFormChange(e) {
        const { name, value } = e.target;
        setAdminCreateForm((f) => ({ ...f, [name]: value }));
    }

    async function handleAdminCreateUser() {
        const errors = [];
        if (!adminCreateForm.email.trim()) {
            errors.push("Email is required");
        }
        if (!adminCreateForm.user_name.trim()) {
            errors.push("Display name is required");
        }
        
        if (errors.length > 0) {
            setError(errors.join(", "));
            return;
        }
        setLoading(true);
        setError("");
        try {
            const response = await apiClient.post("/admin/create_user", {
                action: "create_user",
                email: adminCreateForm.email.trim(),
                user_name: adminCreateForm.user_name.trim(),
                role: adminCreateForm.role
            });
            await fetchUsers();
            setAdminCreateForm({ email: "", user_name: "", role: "Provider" });
            setAdminCreateModalOpen(false);
            setSuccess(`User ${adminCreateForm.email.trim()} created successfully! They will receive an email with a temporary password and must change it on first login.`);
        } catch (e) {
            setError("Failed to create user");
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(email) {
        if (!email) {
            setError("Cannot delete user without email");
            return;
        }
        
        if (user && user.email && user.email.toLowerCase() === email.toLowerCase()) {
            setError("You cannot delete your own account");
            return;
        }

        const userData = users.find(u => u.email && u.email.toLowerCase() === email.toLowerCase());
        if (!userData) {
            setError("User not found");
            return;
        }
        
        if (!window.confirm(`Are you sure you want to delete user ${email}?`)) {
            return;
        }
        setLoading(true);
        setError("");
        try {
            await apiClient.delete("/invites", {
                email: email,
            });
            await fetchUsers();
            setSuccess(`User ${email} deleted successfully`);
        } catch (e) {
            setError("Failed to delete user");
        } finally {
            setLoading(false);
        }
    }

    async function handleEdit(userData) {
        if (!userData.email) {
            setError("Cannot edit user without email");
            return;
        }
        
        
        setEditingUser(userData);
        setEditForm({
            user_name: userData.user_name || "",
            preferences: userData.preferences || "",
            role: userData.role || "Provider",
            user_color: userData.user_color || "blue",
            max_monthly_hours: userData.max_monthly_hours != null ? String(userData.max_monthly_hours) : ""
        });
        setEditModalOpen(true);
    }

    async function handleSaveEdit() {
        if (!editingUser || !editingUser.email) {
            setError("Cannot edit user without email");
            return;
        }

        let maxMonthlyHours = null;
        if (editForm.max_monthly_hours !== "" && editForm.max_monthly_hours != null) {
            const parsed = Number(editForm.max_monthly_hours);
            if (!Number.isInteger(parsed) || parsed < 0 || parsed > 744) {
                setError("Max hours/month must be a whole number between 0 and 744");
                return;
            }
            maxMonthlyHours = parsed;
        }

        setLoading(true);
        setError("");
        try {
            const userData = {
                user_name: editForm.user_name,
                preferences: editForm.preferences || null,
                role: editForm.role,
                user_color: editForm.user_color,
                max_monthly_hours: maxMonthlyHours
            };

            await apiClient.post("/user_profile", {
                action: "update_user_by_manager",
                target_user_id: editingUser.user_id,
                user_data: JSON.stringify(userData)
            });

            setEditModalOpen(false);
            setEditingUser(null);
            setSuccess("User profile updated successfully");
            await fetchUsers();
        } catch (e) {
            setError("Failed to update user profile");
        } finally {
            setLoading(false);
        }
    }

    function formatDate(dateString) {
        if (!dateString) return 'Unknown';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (e) {
            return 'Invalid Date';
        }
    }

    function handleSort(field) {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }

    function getSortedUsers() {
        return [...users].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortField) {
                case 'name':
                    aValue = (a.user_name || a.email || '').toLowerCase();
                    bValue = (b.user_name || b.email || '').toLowerCase();
                    break;
                case 'role':
                    aValue = a.role || '';
                    bValue = b.role || '';
                    break;
                case 'created_at':
                    aValue = a.created_at ? new Date(a.created_at) : new Date(0);
                    bValue = b.created_at ? new Date(b.created_at) : new Date(0);
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }

    function highlightText(text, searchTerm) {
        if (!searchTerm) return text;
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        return text.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
    }

    function getFilteredUsers() {
        if (!searchTerm) return getSortedUsers();
        const lowerSearch = searchTerm.toLowerCase();
        return users.filter(user => {
            const name = (user.user_name || '').toLowerCase();
            const email = (user.email || '').toLowerCase();
            return name.includes(lowerSearch) || email.includes(lowerSearch);
        }).sort((a, b) => {
            let aValue, bValue;
            
            switch (sortField) {
                case 'name':
                    aValue = (a.user_name || a.email || '').toLowerCase();
                    bValue = (b.user_name || b.email || '').toLowerCase();
                    break;
                case 'role':
                    aValue = a.role || '';
                    bValue = b.role || '';
                    break;
                case 'created_at':
                    aValue = a.created_at ? new Date(a.created_at) : new Date(0);
                    bValue = b.created_at ? new Date(b.created_at) : new Date(0);
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }

    function getSortIcon(field) {
        if (sortField !== field) {
            return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
        }
        return sortDirection === 'asc' 
            ? <ChevronUpIcon className="h-4 w-4 text-blue-600" />
            : <ChevronDownIcon className="h-4 w-4 text-blue-600" />;
    }

    function toggleUserInfo(user) {
        const userId = user.user_id;
        const isExpanded = expandedUsers.has(userId);

        if (isExpanded) {
            setExpandedUsers(new Set());
        } else {
            setExpandedUsers(new Set([userId]));
        }
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => setAdminCreateModalOpen(true)}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                    >
                        <PlusIcon className="h-5 w-5" />
                        Create User
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex justify-between items-center">
                    <span>{error}</span>
                    <button
                        onClick={() => setError("")}
                        className="text-red-700 hover:text-red-900"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            )}

            {success && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex justify-between items-center">
                    <span>{success}</span>
                    <button
                        onClick={() => setSuccess("")}
                        className="text-green-700 hover:text-green-900"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading users...</p>
                </div>
            ) : (
                <div className="overflow-hidden">
                    <div className="px-6 py-4">
                        <h2 className="text-lg font-medium text-gray-900">
                            All Users ({getFilteredUsers().length})
                        </h2>
                        <div className="mt-3">
                            <input
                                type="text"
                                placeholder="Search users by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                        onClick={() => handleSort('name')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Email/Name
                                            {getSortIcon('name')}
                                        </div>
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                        onClick={() => handleSort('role')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Role
                                            {getSortIcon('role')}
                                        </div>
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                        onClick={() => handleSort('created_at')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Created
                                            {getSortIcon('created_at')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                    <th className="w-1"></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                            No users found
                                        </td>
                                    </tr>
                                ) : getFilteredUsers().length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                            No users match the current filter
                                        </td>
                                    </tr>
                                ) : (
                                    getFilteredUsers().map((userData, index) => {
                                        const isCurrentUser = user && user.email && userData.email && user.email.toLowerCase() === userData.email.toLowerCase();
                                        const isExpanded = expandedUsers.has(userData.user_id);
                                        return (
                                            <React.Fragment key={index}>
                                                <tr className={`hover:bg-gray-50 ${isCurrentUser ? 'bg-blue-50' : ''}`}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    <div>
                                                        <div 
                                                            className="font-medium"
                                                            dangerouslySetInnerHTML={{ 
                                                                __html: highlightText(userData.user_name || userData.email || 'No Email', searchTerm) 
                                                            }}
                                                        />
                                                        {userData.user_name && userData.email && (
                                                            <div 
                                                                className="text-xs text-gray-500"
                                                                dangerouslySetInnerHTML={{ 
                                                                    __html: highlightText(userData.email, searchTerm) 
                                                                }}
                                                            />
                                                        )}
                                                        {isCurrentUser && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                                                You
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex flex-wrap gap-1">
                                                        {userData.role ? (
                                                            userData.role.split(',').map((role, idx) => (
                                                                <span key={idx} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${role.trim() === 'Manager'
                                                                        ? 'bg-purple-100 text-purple-800'
                                                                        : role.trim() === 'Observer'
                                                                        ? 'bg-yellow-100 text-yellow-800'
                                                                        : role.trim() === 'revoked'
                                                                        ? 'bg-red-100 text-red-800'
                                                                        : 'bg-green-100 text-green-800'
                                                                    }`}>
                                                                    {role.trim()}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">No Role</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {userData.created_at ? formatDate(userData.created_at) : 'Unknown'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex gap-2">
                                                        {userData.email ? (
                                                            <button
                                                                onClick={() => handleEdit(userData)}
                                                                className="text-blue-600 hover:text-blue-900 font-medium flex items-center"
                                                                disabled={loading}
                                                                title="Edit user"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="text-gray-300 cursor-not-allowed font-medium flex items-center"
                                                                disabled
                                                                title="Cannot edit user without email"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                        {isCurrentUser ? (
                                                            <span className="text-gray-300 flex items-center" title="Cannot delete your own account"><TrashIcon className="h-4 w-4" /></span>
                                                        ) : userData.email ? (
                                                            <button
                                                                onClick={() => handleDelete(userData.email)}
                                                                className="text-red-600 hover:text-red-900 font-medium flex items-center"
                                                                disabled={loading}
                                                                title="Delete user"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="text-gray-300 cursor-not-allowed font-medium flex items-center"
                                                                disabled
                                                                title="No email available"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                    <button
                                                        onClick={() => toggleUserInfo(userData)}
                                                        className="flex items-center text-blue-600 hover:text-blue-900 font-medium transition-colors mr-4"
                                                        title={expandedUsers.has(userData.user_id) ? "Hide info" : "Show user info"}
                                                    >
                                                        {expandedUsers.has(userData.user_id) ? (
                                                            <ChevronDownIcon className="h-4 w-4" />
                                                        ) : (
                                                            <ChevronRightIcon className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="5" className="px-0 py-0 bg-gray-50">
                                                            <div className="px-6 py-4 transition-all duration-300 ease-in-out">
                                                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                                                        <div>
                                                                            <div className="space-y-1 text-sm text-gray-600 ml-3">
                                                                                <p><span className="font-medium">Email:</span> {userData.email || "Not specified"}</p>
                                                                                <p><span className="font-medium">User ID:</span> {userData.user_id || "Not specified"}</p>
                                                                                <p><span className="font-medium">Role:</span> {userData.role || "Not specified"}</p>
                                                                                <p><span className="font-medium">Created:</span> {userData.created_at ? formatDate(userData.created_at) : "Unknown"}</p>
                                                                                <div className="flex items-center gap-2 mt-2">
                                                                                    <span className="font-medium">Color:</span>
                                                                                    <div 
                                                                                        className="w-4 h-4 rounded-full border border-gray-300"
                                                                                        style={{
                                                                                            backgroundColor: COLOR_HEX_MAP[userData.user_color] || '#3b82f6'
                                                                                        }}
                                                                                    ></div>
                                                                                    <span className="text-sm text-gray-600">{userData.user_color || 'blue'}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="mr-3">
                                                                            <h4 className="text-sm font-medium text-gray-700 mb-2 mt-2">Work Preferences:</h4>
                                                                            {userData.preferences ? (
                                                                                <div className="ml-4">
                                                                                    <pre className="whitespace-pre-wrap text-sm text-gray-600 font-mono">
                                                                                        {userData.preferences}
                                                                                    </pre>
                                                                                </div>
                                                                            ) : (
                                                                                <p className="ml-4 text-gray-500 text-sm">No preferences set</p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {editModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-7xl p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => setEditModalOpen(false)}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">
                            Edit User
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Email</label>
                                <input
                                    type="email"
                                    value={editingUser?.email || "No email available"}
                                    className="w-full border rounded px-3 py-2 bg-gray-100"
                                    disabled
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Role</label>
                                <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto">
                                    {['Provider', 'Manager', 'Observer'].map(role => {
                                        const isSelected = editForm.role.split(',').map(r => r.trim()).includes(role);
                                        return (
                                            <div
                                                key={role}
                                                className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                    isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                }`}
                                                onClick={() => {
                                                    const currentRoles = editForm.role.split(',').map(r => r.trim()).filter(r => r);
                                                    if (isSelected) {
                                                        const newRoles = currentRoles.filter(r => r !== role);
                                                        setEditForm(prev => ({ ...prev, role: newRoles.join(', ') }));
                                                    } else {
                                                        const newRoles = [...currentRoles, role];
                                                        setEditForm(prev => ({ ...prev, role: newRoles.join(', ') }));
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                                        isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                                                    }`}>
                                                        {isSelected && (
                                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className="text-sm font-medium">{role}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Select one or more roles for this user
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Display Name</label>
                                <input
                                    type="text"
                                    name="user_name"
                                    value={editForm.user_name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, user_name: e.target.value }))}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter display name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Max hours/month</label>
                                <input
                                    type="number"
                                    name="max_monthly_hours"
                                    min="0"
                                    max="744"
                                    step="1"
                                    value={editForm.max_monthly_hours}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, max_monthly_hours: e.target.value }))}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Leave blank for no cap"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Maximum scheduled hours per calendar month. Used to flag overbooked providers when saving drafts.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Work Preferences</label>
                                <textarea
                                    name="preferences"
                                    value={editForm.preferences}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, preferences: e.target.value }))}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                                    placeholder="Available every Monday, Tuesday, Wednesday, Friday and Saturday from 9 AM to 5 PM. Prefer morning shifts, can work weekends if needed."
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Enter work preferences in natural language. This will be used for LLM scheduling prompts.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">User Color</label>
                                <div className="grid grid-cols-12">
                                    {USER_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setEditForm(prev => ({ ...prev, user_color: color }))}
                                            className={`w-8 h-8 rounded-full border-2 transition-all ${
                                                editForm.user_color === color 
                                                    ? 'border-gray-800 scale-110' 
                                                    : 'border-gray-300 hover:border-gray-500'
                                            }`}
                                            style={{
                                                backgroundColor: COLOR_HEX_MAP[color] || '#3b82f6'
                                            }}
                                        >
                                            {editForm.user_color === color && (
                                                <svg className="w-4 h-4 mx-auto text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                    onClick={() => setEditModalOpen(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded border-none flex items-center gap-2"
                                    onClick={handleSaveEdit}
                                    disabled={loading || !editingUser?.email}
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckIcon className="h-4 w-4" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {adminCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-md p-6 rounded shadow relative">
                        <button
                            className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                            onClick={() => setAdminCreateModalOpen(false)}
                        >
                            <XMarkIcon className="h-6 w-6 text-gray-500" />
                        </button>
                        <h2 className="text-xl font-bold mb-4">Create User Account</h2>
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                <p className="text-sm text-blue-800">
                                    <strong>Create User:</strong> This immediately creates a user account in the system. The user will receive an email with a temporary password and can log in right away.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={adminCreateForm.email}
                                    onChange={handleAdminCreateFormChange}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                    placeholder="Enter user's email address"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Display Name</label>
                                <input
                                    type="text"
                                    name="user_name"
                                    value={adminCreateForm.user_name}
                                    onChange={handleAdminCreateFormChange}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                                    placeholder="Enter display name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Role</label>
                                <div className="border rounded p-3 min-h-[100px] max-h-[200px] overflow-y-auto">
                                    {['Provider', 'Manager', 'Observer'].map(role => {
                                        const isSelected = adminCreateForm.role.split(',').map(r => r.trim()).includes(role);
                                        return (
                                            <div
                                                key={role}
                                                className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${
                                                    isSelected ? 'bg-blue-50 border border-blue-200' : ''
                                                }`}
                                                onClick={() => {
                                                    const currentRoles = adminCreateForm.role.split(',').map(r => r.trim()).filter(r => r);
                                                    if (isSelected) {
                                                        const newRoles = currentRoles.filter(r => r !== role);
                                                        setAdminCreateForm(prev => ({ ...prev, role: newRoles.join(', ') }));
                                                    } else {
                                                        const newRoles = [...currentRoles, role];
                                                        setAdminCreateForm(prev => ({ ...prev, role: newRoles.join(', ') }));
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                                        isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                                                    }`}>
                                                        {isSelected && (
                                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className="text-sm font-medium">{role}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Select one or more roles for this user
                                </p>
                            </div>
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                                <p className="text-sm text-yellow-800">
                                    <strong>Note:</strong> This will create a user account immediately. The user will receive an email with a temporary password and will be required to change it on first login.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded border-none"
                                    onClick={() => setAdminCreateModalOpen(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded border-none"
                                    onClick={handleAdminCreateUser}
                                    disabled={loading || !adminCreateForm.email.trim() || !adminCreateForm.user_name.trim()}
                                >
                                    {loading ? "Creating..." : "Create User"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 